import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { from } from 'rxjs';
import { Site } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { AddEditSiteModalComponent } from 'src/app/modals/add-edit-site-modal/add-edit-site-modal.component';
import { AdminClientSiteDetailsModalComponent } from 'src/app/modals/admin-client-site-details-modal/admin-client-site-details-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { FileUploadModalComponent } from 'src/app/modals/file-upload-modal/file-upload-modal.component';
import { SitesUploadModalComponent } from 'src/app/modals/sites-upload-modal/sites-upload-modal.component';
import { CompanyService } from 'src/app/services/company.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';

@Component({
  selector: 'app-admin-client-sites',
  templateUrl: './admin-client-sites.component.html',
  styleUrls: ['./admin-client-sites.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminClientSitesComponent {
  constructor(
    private CompanyService: CompanyService,
    private SharedDataService: SharedDataService,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private SiteService: SiteService,
    private UtilityService: UtilityService,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {
    this.SharedDataService.listenForEvent<number>().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'SetCompanyId' && typeof data === 'number') {
          this.CompanyId = data;

          Object.assign(this.SuppliedGridQuery, { CompanyId: this.CompanyId });

          from(
            companiesService.findOneById({
              pathParams: { companyId: this.CompanyId }
            })
          ).subscribe({
            next: (Company) => {
              const { legalName } = Company;

              this.SharedDataService.sendEvent(
                'SetTitle',
                `Sites - ${legalName}`
              );
              this.ChangeDetectorRef.markForCheck();
            }
          });
        }
      }
    });

    this.SharedDataService.sendEvent('GetCompanyId');
  }

  CompanyId?: number;
  reloadGrid: boolean = false;
  showDeactivated: boolean = false;
  SuppliedGridQuery: GenericObject = {
    attributes: [
      'id',
      'ein',
      'siteKey',
      'phone',
      'phoneExt',
      'fax',
      'units',
      'CompanyId',
      'status'
    ],
    StreetAddress: {
      attributes: ['id']
    }
  };
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit', 'view', 'delete']
  };

  onShowDeactivatedSitesChange() {
    this.showDeactivated = !this.showDeactivated;

    if (this.showDeactivated) {
      Object.assign(this.SuppliedGridQuery, {
        status: { $in: ['Active', 'Deactivated'] }
      });
    }

    if (!this.showDeactivated) {
      Object.assign(this.SuppliedGridQuery, { status: 'Active' });
    }

    this.reloadGrid = true;
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: Site;
  }) {
    const { id: SiteId } = rowData;

    if (!SiteId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot perform action. No site ID found.',
        { returnError: true }
      );
    }

    if (action === 'edit') {
      const { componentInstance, result } = this.ModalService.open(
        AddEditSiteModalComponent,
        { centered: true, size: 'xl' }
      );

      Object.assign(componentInstance, { Site: rowData });

      from(result).subscribe({
        next: (Site: Site) => {
          this.SiteService.saveSite(Site).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully saved site.'
              );
              this.reloadGrid = true;
              this.ChangeDetectorRef.markForCheck();
            }
          });
        }
      });
    }

    if (action === 'view') {
      if (!this.CompanyId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot view site details. No Company ID found.',
          { returnError: true }
        );
      }

      const modalRef = this.ModalService.open(
        AdminClientSiteDetailsModalComponent,
        { centered: true, size: 'xl', scrollable: true }
      );
      Object.assign(modalRef.componentInstance, {
        SiteId,
        CompanyId: this.CompanyId
      });
    }

    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md' }
      );
      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to delete this site?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            this.SiteService.deleteSite({ id: SiteId }).subscribe({
              next: () => {
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully deleted site.'
                );
                this.reloadGrid = true;
                this.ChangeDetectorRef.markForCheck();
              }
            });
          }
        }
      });
    }
  }

  getUploadTemplate() {
    this.SiteService.getSitesUploadTemplate().subscribe({
      next: (sitesTemplateFile) => {
        const base64String = sitesTemplateFile;
        const arrayBuffer =
          this.UtilityService.convertBase64StringToArrayBuffer(base64String);
        const unit8Array = new Uint8Array(arrayBuffer);
        const blob = new Blob([unit8Array], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const fileName = 'SitesUploadTemplate.xlsx';
        saveAs(blob, fileName);
      }
    });
  }

  uploadSites() {
    const modalRef = this.ModalService.open(FileUploadModalComponent, {
      centered: true,
      size: 'lg'
    });
    Object.assign(modalRef.componentInstance, { title: 'Upload Sites File' });

    modalRef.closed.subscribe({
      next: (Files) => {
        if (Files) {
          if (Files.length > 1) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Cannot process sites. Only one file can be processed.',
              { returnError: true }
            );
          }

          const sitesFile = Files[0];

          this.SiteService.processSitesUpload(sitesFile, {
            CompanyId: this.CompanyId
          }).subscribe({
            next: (Sites: Site[]) => {
              const modalRef = this.ModalService.open(
                SitesUploadModalComponent,
                { centered: true, size: 'xl' }
              );
              Object.assign(modalRef.componentInstance, { Sites });

              modalRef.closed.subscribe({
                next: () => {
                  this.reloadGrid = true;
                }
              });
            }
          });
        }
      }
    });
  }
}
